/* import __COLOCATED_TEMPLATE__ from './workflow-side-sheet.hbs'; */
/* RESPONSIBLE TEAM: team-ml */
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type Workflow from 'embercom/models/operator/visual-builder/workflow';
import SdrRoutingOutcomeEditorConfig from 'embercom/objects/visual-builder/configuration/editor/sdr-routing-outcome';
import type Store from '@ember-data/store';
import type ReactTeammateAppIslandService from 'embercom/services/react-teammate-app-island';
import type IntlService from 'embercom/services/intl';
import type WorkflowConnectorInsertableActionsService from 'embercom/services/workflow-connector-insertable-actions';
import { dropTask } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import generateUUID from 'embercom/lib/uuid-generator';
import { ROOT_PARENT_SELECTOR } from '@intercom/pulse/lib/popover-utils';

type WorkflowParams = Record<string, unknown> | undefined | null;

interface Signature {
  Args: {
    onClose: () => void;
    isOpen: boolean;
    routingOutcomeIdentifier: string;
    routingOutcomeTitle?: string;
    workflowParams?: WorkflowParams;
  };
}

export default class SalesDevelopmentWorkflowSideSheet extends Component<Signature> {
  @service declare store: Store;
  @service declare reactTeammateAppIsland: ReactTeammateAppIslandService;
  @service declare intl: IntlService;
  @service declare workflowConnectorInsertableActions: WorkflowConnectorInsertableActionsService;
  @service declare appService: $TSFixMe;

  // NEW UI: Element for blanket overlay
  rootParentElement = document.querySelector(ROOT_PARENT_SELECTOR);

  // Check if using NEW UI (Feature Flag ON: sdr-config-setup-v2)
  get useNewUI(): boolean {
    return this.appService.app.canUseFeature('sdr-config-setup-v2');
  }

  @tracked workflow?: Workflow;
  @tracked showConfirmationModal = false;
  @tracked private workflowArgsSignature?: string;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    taskFor(this.loadActionsAndInitializeWorkflow).perform();
  }

  get isLoadingActions() {
    return taskFor(this.loadActionsAndInitializeWorkflow).isRunning;
  }

  @dropTask
  *loadActionsAndInitializeWorkflow(): TaskGenerator<void> {
    try {
      yield this.workflowConnectorInsertableActions.loadActions();
    } catch (e: unknown) {
      console.error('Failed to load insertable actions:', e);
    }
    try {
      yield this.appService.app.loadAndUpdateTags();
    } catch (e: unknown) {
      console.error('Failed to refresh tags:', e);
    }
    this.initializeWorkflowFromArgs();
  }

  get editorConfiguration() {
    return new SdrRoutingOutcomeEditorConfig({
      workflow: this.workflow,
    });
  }

  get infoPanelTriggerData() {
    let title = this.intl.t('ai-agent.sales-development.workflow-trigger.title');
    let subtitle = this.args.routingOutcomeTitle
      ? this.intl.t('ai-agent.sales-development.workflow-trigger.as-outcome', {
          outcome: this.args.routingOutcomeTitle,
        })
      : undefined;

    // NEW UI: Return title and subtitle separately for breadcrumb display
    if (this.useNewUI) {
      return {
        title,
        subtitle,
        icon: 'qual-identify',
      };
    }

    // OLD UI: Combine title and subtitle into single string
    return {
      title: subtitle ? `${title} — ${subtitle}` : title,
      icon: 'qual-identify',
    };
  }

  get isWorkflowValid() {
    return this.workflow?.validations.isValid ?? false;
  }

  get isSaveDisabled() {
    return !this.isWorkflowValid;
  }

  get hasUnsavedChanges() {
    return !!this.workflow?.hasUnsavedChanges;
  }

  @action
  handleClose() {
    if (this.hasUnsavedChanges) {
      this.showConfirmationModal = true;
      return;
    }

    // NEW UI: Notify React that the workflow sheet closed
    if (this.useNewUI) {
      this.reactTeammateAppIsland.dispatchTeammateAppEvent('fin-sdr:workflow-closed');
    }

    this.args.onClose();
  }

  @action
  confirmClose() {
    this.showConfirmationModal = false;

    // NEW UI: Notify React that the workflow sheet closed
    if (this.useNewUI) {
      this.reactTeammateAppIsland.dispatchTeammateAppEvent('fin-sdr:workflow-closed');
    }

    if (this.workflow) {
      this.workflow.rollbackAttributes();
      this.args.onClose();
    } else {
      this.args.onClose();
    }
  }

  @action
  cancelClose() {
    this.showConfirmationModal = false;
  }

  @action
  saveWorkflow() {
    if (!this.workflow) {
      return;
    }

    let workflowPayload = this.serializeWorkflow();
    if (!workflowPayload) {
      return;
    }
    this.reactTeammateAppIsland.dispatchTeammateAppEvent('fin-sdr:workflow-updated', {
      routingOutcomeIdentifier: this.args.routingOutcomeIdentifier,
      workflowParams: workflowPayload,
    });
    this.args.onClose();
  }

  @action
  deleteWorkflow() {
    this.reactTeammateAppIsland.dispatchTeammateAppEvent('fin-sdr:workflow-updated', {
      routingOutcomeIdentifier: this.args.routingOutcomeIdentifier,
      workflowParams: null,
    });
    // Notify React to update CTA immediately for backwards compatibility
    this.reactTeammateAppIsland.dispatchTeammateAppEvent('fin-sdr:workflow-deleted', {
      routingOutcomeIdentifier: this.args.routingOutcomeIdentifier,
    });
    this.args.onClose();
  }

  @action
  handleWorkflowArgsChange() {
    this.initializeWorkflowFromArgs();
  }

  private initializeWorkflowFromArgs() {
    let signature = JSON.stringify({
      routingOutcomeIdentifier: this.args.routingOutcomeIdentifier,
      workflowParams: this.args.workflowParams,
    });

    if (signature === this.workflowArgsSignature) {
      return;
    }

    this.workflowArgsSignature = signature;

    if (this.workflow) {
      this.workflow.unloadRecord();
      this.workflow = undefined;
    }

    if (this.args.workflowParams) {
      this.workflow = this.pushWorkflowPayload(this.args.workflowParams);
    } else {
      this.workflow = this.createEmptyWorkflowRecord();
    }
  }

  private pushWorkflowPayload(payload: Record<string, unknown>) {
    if (!payload.id) {
      payload.id = generateUUID();
    }

    let existingWorkflow = this.store
      .peekAll('operator/visual-builder/workflow')
      .find((w) => w._uuid === payload.id || w.id === payload.id) as Workflow | null;

    if (existingWorkflow?.groups?.firstObject?.steps?.length) {
      return existingWorkflow;
    }

    this.store.pushPayload({ 'operator/visual-builder/workflow': payload });

    return this.store.peekRecord(
      'operator/visual-builder/workflow',
      payload.id as string,
    ) as Workflow;
  }

  private createEmptyWorkflowRecord() {
    let workflow = this.store.createRecord('operator/visual-builder/workflow');
    let group = this.store.createRecord('operator/visual-builder/group', {
      isStart: true,
      title: null,
    });

    group.steps.clear();
    workflow.groups.pushObject(group);

    return workflow;
  }

  private serializeWorkflow() {
    if (!this.workflow) {
      return null;
    }

    return this.workflow.serialize();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::SalesDevelopment::WorkflowSideSheet': typeof SalesDevelopmentWorkflowSideSheet;
    'ai-agent/sales-development/workflow-side-sheet': typeof SalesDevelopmentWorkflowSideSheet;
  }
}
